import { effectiveExecutionProfile } from "./effectiveExecutionProfile";

const LOAD_MODES = new Set([
  "NONE",
  "PER_IMPLEMENT",
  "OLYMPIC_BARBELL",
  "BAR_AS_ENTERED",
  "LOADED_END",
  "SMITH_PER_SIDE",
  "PLATE_LOADED",
  "MACHINE_VALUE",
  "ADDED_WEIGHT",
  "ASSISTANCE",
  "OPTIONAL_WEIGHT",
]);

const EXPECTED_CONVENTIONS = Object.freeze({
  PER_IMPLEMENT: ["PER_IMPLEMENT"],
  OLYMPIC_BARBELL: ["TOTAL", "PER_SIDE"],
  BAR_AS_ENTERED: ["AS_ENTERED"],
  LOADED_END: ["LOADED_END"],
  SMITH_PER_SIDE: ["PER_SIDE"],
  PLATE_LOADED: ["PLATES_LOADED"],
  MACHINE_VALUE: ["MACHINE_VALUE"],
  ADDED_WEIGHT: ["ADDED"],
  ASSISTANCE: ["ASSISTANCE"],
  OPTIONAL_WEIGHT: ["OPTIONAL_WEIGHT"],
});

function isIntegerInRange(value, min, max) {
  return Number.isInteger(value) && value >= min && value <= max;
}

function isDecimalString(value) {
  return typeof value === "string" &&
    /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(value.trim()) &&
    Number(value) >= 0 && Number(value) <= 2000;
}

function allowsZero(loadMode, convention) {
  return [
    "LOADED_END",
    "SMITH_PER_SIDE",
    "PLATE_LOADED",
    "ADDED_WEIGHT",
    "ASSISTANCE",
  ].includes(loadMode) ||
    (loadMode === "OLYMPIC_BARBELL" && convention === "PER_SIDE");
}

function isValidBarWeight(barWeight, unit) {
  if (!barWeight || barWeight.unit !== unit || !isDecimalString(barWeight.value)) return false;
  const amount = Number(barWeight.value);
  return unit === "KG"
    ? amount >= 5 && amount <= 35
    : amount >= 10 && amount <= 80;
}

export function validateLoad(load, loadMode) {
  if (!LOAD_MODES.has(loadMode)) return false;
  if (load == null) return true;
  if (loadMode === "NONE") return false;
  if (!isDecimalString(load.value) || !["LB", "KG"].includes(load.unit)) return false;
  if (!EXPECTED_CONVENTIONS[loadMode]?.includes(load.convention)) return false;
  if (Number(load.value) === 0 && !allowsZero(loadMode, load.convention)) return false;
  if (loadMode === "OLYMPIC_BARBELL" && load.convention === "PER_SIDE") {
    return isValidBarWeight(load.barWeight, load.unit);
  }
  return load.barWeight == null;
}

export function evaluatePerformedSet({
  measurementType,
  loadMode,
  set = {},
  rirInputEnabled = true,
  previousPerformedAt = null,
} = {}) {
  if (!["REPS", "TIMED_HOLD"].includes(measurementType) || !LOAD_MODES.has(loadMode)) {
    return { valid: false, performed: false, performedAt: previousPerformedAt };
  }
  if (set.rir != null && (!rirInputEnabled || measurementType !== "REPS" ||
    !isIntegerInRange(set.rir, 0, 5))) {
    return { valid: false, performed: false, performedAt: previousPerformedAt };
  }
  if (!validateLoad(set.load ?? null, loadMode)) {
    return { valid: false, performed: false, performedAt: previousPerformedAt };
  }

  if (measurementType === "TIMED_HOLD") {
    if (set.reps != null || set.rir != null ||
      (set.seconds != null && !isIntegerInRange(set.seconds, 1, 3600))) {
      return { valid: false, performed: false, performedAt: previousPerformedAt };
    }
    return {
      valid: true,
      performed: set.seconds != null,
      performedAt: previousPerformedAt,
    };
  }

  if (set.seconds != null ||
    (set.reps != null && !isIntegerInRange(set.reps, 1, 100))) {
    return { valid: false, performed: false, performedAt: previousPerformedAt };
  }
  const performed = loadMode === "NONE"
    ? set.reps != null
    : set.load != null && set.reps != null;
  return { valid: true, performed, performedAt: previousPerformedAt };
}

export function evaluatePerformedCardio({ cardio = {}, cardioGhostMinutes = null } = {}) {
  if (cardio.minutes != null && !isIntegerInRange(cardio.minutes, 1, 600)) {
    return { valid: false, performed: false, minutes: cardio.minutes };
  }
  if (!cardio.completed) {
    return { valid: true, performed: false, minutes: cardio.minutes ?? null };
  }
  const minutes = cardio.minutes ?? cardioGhostMinutes;
  if (!isIntegerInRange(minutes, 1, 600)) {
    return { valid: false, performed: false, minutes: minutes ?? null };
  }
  return { valid: true, performed: true, minutes };
}

export function evaluatePerformedRule(entry = {}) {
  if (entry.measurementType === "CARDIO_MINUTES") {
    return evaluatePerformedCardio(entry);
  }
  const result = evaluatePerformedSet(entry);
  return {
    ...result,
    ...(entry.previousPerformedAt
      ? { preservedPerformedAt: entry.previousPerformedAt }
      : {}),
  };
}

// Performed work of a local execution record (§8.1), judged with each item's
// effective execution profile (§15.8) so finalization counts exactly what the
// execution surface and the reducer consider performed.
export function countPerformed(record = {}) {
  return (record.document?.items || []).reduce((total, item) => {
    const profile = effectiveExecutionProfile(record, item);
    if (profile?.measurementType === "CARDIO_MINUTES") {
      return total + (evaluatePerformedCardio({ cardio: item.cardio }).performed ? 1 : 0);
    }
    return total + (item.sets || []).filter((set) => evaluatePerformedSet({
      measurementType: profile?.measurementType,
      loadMode: profile?.loadMode,
      set,
      rirInputEnabled: true,
    }).performed).length;
  }, 0);
}

export { LOAD_MODES };
